
import React, { createContext, useReducer, useContext, useEffect } from 'react';

const SearchHeaderContext = createContext();

const initialState = {
  products: [],
  collections: [],
  suggestedQueries: [],
  filters: [],
  activeFilters: {},
  sortOption: 'RELEVANCE',
  loading: false,
  totalResults: 0,
  currentPage: 1,
  isLastPage: false,
  productsPerPage: 8,
  searchQuery: ''
};

const actionTypes = {
  SET_PRODUCTS: 'SET_PRODUCTS',
  SET_COLLECTIONS: 'SET_COLLECTIONS',
  SET_SUGGESTED_QUERIES: 'SET_SUGGESTED_QUERIES',
  SET_FILTERS: 'SET_FILTERS',
  SET_ACTIVE_FILTERS: 'SET_ACTIVE_FILTERS',
  SET_SORT_OPTION: 'SET_SORT_OPTION',
  SET_LOADING: 'SET_LOADING',
  SET_TOTAL_RESULTS: 'SET_TOTAL_RESULTS',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_IS_LAST_PAGE: 'SET_IS_LAST_PAGE',
  SET_PRODUCTS_PER_PAGE: 'SET_PRODUCTS_PER_PAGE',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
};

function searchHeaderReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_PRODUCTS:
      return { ...state, products: action.payload };
    case actionTypes.SET_COLLECTIONS:
      return { ...state, collections: action.payload };
    case actionTypes.SET_SUGGESTED_QUERIES:
      return { ...state, suggestedQueries: action.payload };
    case actionTypes.SET_FILTERS:
      return { ...state, filters: action.payload };
    case actionTypes.SET_ACTIVE_FILTERS:
      return { ...state, activeFilters: action.payload };
    case actionTypes.SET_SORT_OPTION:
      return { ...state, sortOption: action.payload };
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case actionTypes.SET_TOTAL_RESULTS:
      return { ...state, totalResults: action.payload };
    case actionTypes.SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload };
    case actionTypes.SET_IS_LAST_PAGE:
      return { ...state, isLastPage: action.payload };
    case actionTypes.SET_PRODUCTS_PER_PAGE:
      return { ...state, productsPerPage: action.payload };
    case actionTypes.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };
    default:
      return state;
  }
}

export const SearchHeaderProvider = ({ children, defaultSettings = {} }) => {
  const [state, dispatch] = useReducer(searchHeaderReducer, {
    ...initialState,
    productsPerPage: defaultSettings.productsPerPage || initialState.productsPerPage,
    sortOption: defaultSettings.defaultSortOption || initialState.sortOption,
  });

  useEffect(() => {
    const handleSearchComplete = (event) => {
      const { products, totalResults, query } = event.detail;
      // Always capture search data regardless of type to ensure searchQuery is available
      dispatch({ type: actionTypes.SET_PRODUCTS, payload: products });
      dispatch({ type: actionTypes.SET_TOTAL_RESULTS, payload: totalResults });
      dispatch({ type: actionTypes.SET_SEARCH_QUERY, payload: query });
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
    };

    const handleSearchInitiated = (event) => {
      const { page, productsPerPage } = event.detail;
      if (event.detail.type == 'header-search') {
        dispatch({ type: actionTypes.SET_CURRENT_PAGE, payload: page || 1 });
        dispatch({ type: actionTypes.SET_PRODUCTS_PER_PAGE, payload: productsPerPage || initialState.productsPerPage });
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
        dispatch({ type: actionTypes.SET_IS_LAST_PAGE, payload: false });
      }
    };

    const handleFilterApplied = (event) => {
      const { filters_object, type } = event.detail;
      if (type === 'header-search') {
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
      }
    };

    const handleSearchCleared = (event) => {
      // Reset all search-related state when search is cleared
      dispatch({ type: actionTypes.SET_PRODUCTS, payload: [] });
      dispatch({ type: actionTypes.SET_COLLECTIONS, payload: [] });
      dispatch({ type: actionTypes.SET_SUGGESTED_QUERIES, payload: [] });
      dispatch({ type: actionTypes.SET_TOTAL_RESULTS, payload: 0 });
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
      dispatch({ type: actionTypes.SET_SEARCH_QUERY, payload: '' });
    };

    // Listen for searchQuery changes and reset to default if empty
    let lastSearchQuery = '';
    const handleCollectionsComplete = (event) => {
      const { collections, query, queries } = event.detail;
      // Always capture collections and suggested queries regardless of type
      dispatch({ type: actionTypes.SET_COLLECTIONS, payload: collections || [] });
      dispatch({ type: actionTypes.SET_SUGGESTED_QUERIES, payload: queries || [] });
    };

    // NOTE: Temporarily disabled this handler as it's causing collections/suggestedQueries to be reset
    // The window.searchHeaderContext.searchQuery doesn't seem to be maintained properly
    const handleSearchQueryChange = () => {
      // const searchQuery = window?.searchHeaderContext?.searchQuery || '';
      // console.log('SearchHeaderContext: handleSearchQueryChange triggered');
      // console.log('SearchHeaderContext: searchQuery:', searchQuery);
      // console.log('SearchHeaderContext: lastSearchQuery:', lastSearchQuery);
      
      // if (searchQuery !== lastSearchQuery) {
      //   lastSearchQuery = searchQuery;
      //   if (!searchQuery || searchQuery.length === 0) {
      //     console.log('SearchHeaderContext: Resetting collections and suggestedQueries because search query is empty');
      //     // Reset products/categories to default (popular products, categories, etc)
      //     dispatch({ type: actionTypes.SET_PRODUCTS, payload: [] });
      //     dispatch({ type: actionTypes.SET_COLLECTIONS, payload: [] });
      //     dispatch({ type: actionTypes.SET_SUGGESTED_QUERIES, payload: [] });
      //     dispatch({ type: actionTypes.SET_TOTAL_RESULTS, payload: 0 });
      //     dispatch({ type: actionTypes.SET_LOADING, payload: false });
      //   }
      // }
    };
    window.addEventListener('searchrequest', handleSearchInitiated);
    window.addEventListener('searchrequest-products-complete', handleSearchComplete);
    window.addEventListener('searchrequest-collections-complete', handleCollectionsComplete);
    window.addEventListener('searchrequest-filter-applied', handleFilterApplied);
    window.addEventListener('search-header-query-change', handleSearchQueryChange);
    window.addEventListener('search-cleared', handleSearchCleared);
    return () => {
      window.removeEventListener('searchrequest-products-complete', handleSearchComplete);
      window.removeEventListener('searchrequest-collections-complete', handleCollectionsComplete);
      window.removeEventListener('searchrequest', handleSearchInitiated);
      window.removeEventListener('searchrequest-filter-applied', handleFilterApplied);
      window.removeEventListener('search-header-query-change', handleSearchQueryChange);
      window.removeEventListener('search-cleared', handleSearchCleared);
    };
  }, []);

  return (
    <SearchHeaderContext.Provider value={{ ...state, dispatch, actionTypes }}>
      {children}
    </SearchHeaderContext.Provider>
  );
};

export const useSearchHeader = () => {
  const context = useContext(SearchHeaderContext);
  if (!context) {
    throw new Error('useSearchHeader must be used within a SearchHeaderProvider');
  }
  return context;
};

export default SearchHeaderContext;